import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../../components/common/PageHeader';
import { useAdminListOrganizations } from '../../api/generated/generated';
import { CreateOrgDialog } from './CreateOrgDialog';

export function AdminOrgsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [nameFilter, setNameFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: orgsResp, isLoading, error } = useAdminListOrganizations(
    { name: nameFilter || undefined, limit: rowsPerPage, offset: page * rowsPerPage },
    { query: { staleTime: 60_000 } },
  );
  const data = orgsResp?.status === 200 ? orgsResp.data : undefined;

  if (!user?.superAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        You do not have permission to access this page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageHeader title="Organizations" />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Organization
        </Button>
      </Box>
      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <TextField
        placeholder="Search by name..."
        size="small"
        value={nameFilter}
        onChange={(e) => {
          setNameFilter(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2, width: 300 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load organizations.
        </Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.organizations.map((org) => (
                  <TableRow
                    key={org.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                  >
                    <TableCell>{org.name}</TableCell>
                    <TableCell>{org.owner_email}</TableCell>
                    <TableCell align="right">{org.member_count}</TableCell>
                    <TableCell>
                      {new Date(org.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
                {data?.organizations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        sx={{
                          color: "text.secondary",
                          py: 2
                        }}>
                        No organizations found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
